import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  MigrationLogsQuery,
  NewMigrationLogsSubscription,
} from "@/admin/entities/migrations";
import { CustomCellDate } from "@/shared/components/DateCell";
import type { MigrationLog } from "@/admin/entities/migrations";

const LogsContainer = styled(Box)(() => ({
  maxHeight: 600,
  overflow: "auto",
}));

const LogMessage = styled(Box)(() => ({
  whiteSpace: "pre-wrap",
  fontFamily: "monospace",
  fontSize: "0.8rem",
  margin: 0,
  padding: 0,
}));

interface MigrationLogsProps {
  migrationId: string;
}

export const MigrationLogs: React.FC<MigrationLogsProps> = ({
  migrationId,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const {
    data,
    loading: logsLoading,
    error: logsError,
    subscribeToMore,
  } = useQuery(MigrationLogsQuery, {
    variables: {
      page,
      rowsPerPage,
      filter: {
        migrationId,
      },
    },
    skip: !migrationId,
  });

  useEffect(() => {
    if (!migrationId) return;

    const unsubscribe = subscribeToMore({
      document: NewMigrationLogsSubscription,
      variables: { migrationId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        const newLog = subscriptionData.data.newMigrationLogs;
        const existingLogs = prev?.migrationLogs?.nodes || [];

        const logExists = existingLogs.some(
          (log: MigrationLog) => log.id === newLog.id
        );
        if (logExists) return prev;

        return {
          ...prev,
          migrationLogs: {
            ...prev.migrationLogs,
            nodes: [newLog, ...existingLogs],
            count: (prev.migrationLogs?.count || 0) + 1,
          },
        };
      },
    });

    return () => unsubscribe();
  }, [migrationId, subscribeToMore]);

  const logs = data?.migrationLogs?.nodes || [];
  const logsCount = data?.migrationLogs?.count || 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    event?.preventDefault();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (logsError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Ошибка подключения к логам: {logsError?.message}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h3">
            Логи выполнения в реальном времени
          </Typography>
          {logsLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Загрузка логов...
              </Typography>
            </Box>
          )}
        </Box>

        {logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {logsLoading
              ? "Загрузка логов..."
              : "Логи появятся здесь в реальном времени"}
          </Typography>
        ) : (
          <LogsContainer>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 200 }}>Время</TableCell>
                    <TableCell>Сообщение</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log: MigrationLog) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <CustomCellDate date={log.createdAt} />
                      </TableCell>
                      <TableCell>
                        <LogMessage>{log.message}</LogMessage>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {logsCount > rowsPerPage && (
                <TablePagination
                  component="div"
                  count={logsCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} из ${count}`
                  }
                />
              )}
            </TableContainer>
          </LogsContainer>
        )}

        {logs.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Всего логов: {logsCount}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
